'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { getProductTypeSizeImageSrc } from '@/utility/images';
import { SizeFitHint } from './SizeFitHint';
type Unit = 'cm' | 'in';
type SizeGuideTableProps = {
  className?: string;
  productType: ClientProductType;
  platform: Platform;
  sizeGuideIsInDialog?: boolean;
  isSellableGroup?: boolean;
};
const getLocalizedSize = (value: number, unit: Unit) => {
  if (unit === 'in') {
    return (value / 25.4).toFixed(2);
  }
  return (value / 10).toFixed(1);
};
export const SizeGuide = t0 => {
  const $ = _c(57);
  const {
    className,
    productType,
    platform,
    sizeGuideIsInDialog,
    isSellableGroup: t1
  } = t0;
  const isSellableGroup = t1 === undefined ? false : t1;
  const t = useTranslations();
  const imageBaseUrl = useImageBaseUrl();
  const locale = useLocale();
  const unit = platform === "NA" || locale === "en-GB" ? "in" : "cm";
  const showSizeGuide = productType.sizes.length > 1 && !isSellableGroup;
  let t2;
  if ($[0] !== className) {
    t2 = clsx("flex flex-col gap-4", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== productType.sizeFitHintId || $[3] !== showSizeGuide || $[4] !== sizeGuideIsInDialog || $[5] !== t) {
    t3 = showSizeGuide && <><SizeFitHint sizeFitHintId={productType.sizeFitHintId} className={clsx(sizeGuideIsInDialog ? "text-sm" : "text-base")} /><p><span className={clsx(sizeGuideIsInDialog ? "font-normal" : "font-medium")}>{t("size.guide.hintTitle")}</span><br />{t("size.guide.hintText")}</p></>;
    $[2] = productType.sizeFitHintId;
    $[3] = showSizeGuide;
    $[4] = sizeGuideIsInDialog;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = sizeGuideIsInDialog ? "flex-col sm:flex-row" : "flex-col";
  let t5;
  if ($[7] !== t4) {
    t5 = clsx("flex gap-4", t4);
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = sizeGuideIsInDialog ? "sm:flex-col" : "flex-row";
  let t7;
  if ($[9] !== t6) {
    t7 = clsx("flex items-center justify-center xs:gap-4", t6);
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== imageBaseUrl || $[12] !== productType.id) {
    t8 = getProductTypeSizeImageSrc(imageBaseUrl, productType.id, 500);
    $[11] = imageBaseUrl;
    $[12] = productType.id;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t) {
    t9 = t("size.guide.imageLabel");
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <img className="w-full max-w-60" src={t8} alt={t9} loading="lazy" />;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== productType.sizes[0]?.measures || $[20] !== t || $[21] !== unit) {
    t11 = productType.sizes[0]?.measures.map(measure => {
      const textKey = `size.guide.sizeIn.${measure.name}`;
      return <li key={textKey}>{`${measure.name} - ${t(textKey, {
          unit
        })}`}</li>;
    });
    $[19] = productType.sizes[0]?.measures;
    $[20] = t;
    $[21] = unit;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <ul className="text-sm font-medium">{t11}</ul>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t12 || $[27] !== t7) {
    t13 = <div className={t7}>{t10}{t12}</div>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t7;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("size.guide.size");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <th className="p-2 text-start">{t14}</th>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== productType.sizes[0]?.measures || $[34] !== unit) {
    t16 = productType.sizes[0]?.measures.sort(_temp).map(measure_0 => <th key={measure_0.name} className="p-2">{`${measure_0.name} (${unit})`}</th>);
    $[33] = productType.sizes[0]?.measures;
    $[34] = unit;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <thead className="border-b border-neutral-300"><tr>{t15}{t16}</tr></thead>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== productType.sizes || $[40] !== unit) {
    let t19;
    if ($[42] !== unit) {
      t19 = size => <tr key={size.id} className="even:bg-neutral-100"><td className="p-2">{size.name}</td>{size.measures.sort(_temp2).map(measure_1 => <td key={measure_1.name} className="p-2 text-center">{getLocalizedSize(measure_1.value.value, unit)}</td>)}</tr>;
      $[42] = unit;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    t18 = productType.sizes.map(t19);
    $[39] = productType.sizes;
    $[40] = unit;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[44] !== t18) {
    t19 = <tbody>{t18}</tbody>;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t17 || $[47] !== t19) {
    t20 = <table className="w-full">{t17}{t19}</table>;
    $[46] = t17;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t13 || $[50] !== t20 || $[51] !== t5) {
    t21 = <div className={t5}>{t13}{t20}</div>;
    $[49] = t13;
    $[50] = t20;
    $[51] = t5;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t2 || $[54] !== t21 || $[55] !== t3) {
    t22 = <div className={t2}>{t3}{t21}</div>;
    $[53] = t2;
    $[54] = t21;
    $[55] = t3;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
};
function _temp(a, b) {
  return a.name.localeCompare(b.name);
}
function _temp2(a_0, b_0) {
  return a_0.name.localeCompare(b_0.name);
}